import { useEffect, useState, useMemo } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { Modal, Empty, StatusBadge, FileCCE } from "./ui";
import UniversalImport from "./UniversalImport";
import EvidenceCell from "./EvidenceCell";
import { Pencil, Trash2, Plus, Download, Upload, FileText, Paperclip } from "lucide-react";

/**
 * Generic CRUD table.
 * fields: [{ key, label, type, options?, required?, hidden?, render? }]
 *   type: text | number | date | select | textarea | checkbox | status
 * extraActions: array of {label, onClick(row)}
 * preCreate: function(form) to mutate form before POST
 */
export default function CrudTable({
  collection,
  title,
  fields,
  filters = {},
  testIdPrefix = "crud",
  extraActions = [],
  onRowClick,
  onAfterSave,
  hideAdd = false,
  hideEvidence = false,
  parent,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = { ...filters };
      if (parent) {
        params.parent_id = parent.id;
        params.parent_field = parent.field;
      }
      const { data } = await api.get(`/data/${collection}`, { params });
      setRows(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collection, JSON.stringify(filters), parent?.id]);

  function openNew() {
    const init = {};
    fields.forEach((f) => {
      if (f.default !== undefined) init[f.key] = f.default;
    });
    if (parent) init[parent.field] = parent.id;
    setEditing(null);
    setForm(init);
    setShowModal(true);
  }

  function openEdit(row) {
    setEditing(row);
    setForm({ ...row });
    setShowModal(true);
  }

  async function save(e) {
    e?.preventDefault?.();
    const payload = { ...form };
    delete payload.id; delete payload.tenant_id;
    delete payload.created_at; delete payload.updated_at;
    delete payload._id;
    let saved = null;
    try {
      if (editing) {
        const { data } = await api.patch(`/data/${collection}/${editing.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post(`/data/${collection}`, payload);
        saved = data;
      }
    } catch (err) {
      console.error("Error al guardar:", err);
      const detail = err?.response?.data?.detail || err?.message || "";
      alert("No se pudo guardar el registro. Intenta de nuevo." + (detail ? `\n\nDetalle: ${String(detail).slice(0, 200)}` : ""));
      return;
    }
    setShowModal(false);
    setEditing(null);
    setForm({});
    load();
    if (saved && onAfterSave) {
      try { onAfterSave(saved); } catch (e) { console.error(e); }
    }
  }

  async function del(row) {
    if (!window.confirm(`¿Eliminar este registro?`)) return;
    try {
      await api.delete(`/data/${collection}/${row.id}`);
    } catch (err) {
      console.error(err);
      alert("No se pudo eliminar el registro. Intenta de nuevo.");
      return;
    }
    load();
  }

  const visibleFields = fields.filter((f) => !f.hidden);
  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((r) =>
      visibleFields.some((f) => {
        const v = r[f.key];
        return v != null && String(v).toLowerCase().includes(s);
      })
    );
    // eslint-disable-next-line
  }, [rows, search, fields]);

  return (
    <div data-testid={`${testIdPrefix}-table`}>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          {title && <h2 className="card-h1" style={{ margin: 0 }}>{title}</h2>}
          <input
            className="input"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
            data-testid={`${testIdPrefix}-search`}
          />
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm" onClick={async () => {
            const token = localStorage.getItem("token");
            const audit = localStorage.getItem("audit_tenant_id");
            const headers = { Authorization: `Bearer ${token}` };
            if (audit) headers["X-Audit-Tenant-Id"] = audit;
            const qs = new URLSearchParams();
            Object.entries(filters || {}).forEach(([k,v]) => v !== undefined && v !== null && qs.append(k, v));
            if (parent) { qs.append("parent_id", parent.id); qs.append("parent_field", parent.field); }
            const res = await fetch(`${BACKEND_URL}/api/export/${collection}.xlsx?${qs}`, { headers });
            if (!res.ok) { alert("Error exportando"); return; }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `${collection}.xlsx`; a.click();
            URL.revokeObjectURL(url);
          }} data-testid={`${testIdPrefix}-export`}>
            <Download size={14} /> Exportar XLSX
          </button>
          <button className="btn btn-sm" onClick={async () => {
            const token = localStorage.getItem("token");
            const audit = localStorage.getItem("audit_tenant_id");
            const headers = { Authorization: `Bearer ${token}` };
            if (audit) headers["X-Audit-Tenant-Id"] = audit;
            const qs = new URLSearchParams();
            Object.entries(filters || {}).forEach(([k,v]) => v !== undefined && v !== null && qs.append(k, v));
            if (parent) { qs.append("parent_id", parent.id); qs.append("parent_field", parent.field); }
            if (title) qs.append("title", title);
            const cols = (fields || []).filter((f) => !f.hidden).map((f) => `${f.key}:${f.label}`).join(",");
            if (cols) qs.append("columns", cols);
            const res = await fetch(`${BACKEND_URL}/api/pdf/table/${collection}?${qs}`, { headers });
            if (!res.ok) { alert("Error exportando PDF"); return; }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `${collection}.pdf`; a.click();
            URL.revokeObjectURL(url);
          }} data-testid={`${testIdPrefix}-export-pdf`}>
            <FileText size={14} /> Exportar PDF
          </button>
          <button className="btn btn-sm" onClick={() => setShowImport(true)} data-testid={`${testIdPrefix}-import`}>
            <Upload size={14} /> Importar
          </button>
          {!hideAdd && (
            <button className="btn btn-primary btn-sm" onClick={openNew} data-testid={`${testIdPrefix}-add`}>
              <Plus size={14} /> Nuevo
            </button>
          )}
        </div>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="p-4 text-dim">Cargando...</div>
        ) : !filteredRows.length ? (
          <Empty />
        ) : (
          <table className="table">
            <thead>
              <tr>
                {visibleFields.map((f) => <th key={f.key}>{f.label}</th>)}
                {!hideEvidence && <th style={{ width: 110 }}>Evidencias</th>}
                <th style={{ width: 120, textAlign: "right" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.id}
                  onClick={(e) => {
                    // Single click → preview attachments unless onRowClick is provided
                    if (onRowClick) { onRowClick(row); return; }
                    setPreviewRow(row);
                  }}
                  onDoubleClick={async (e) => {
                    // Double click → download first attachment if any
                    try {
                      const { data: files } = await api.get("/files/list", { params: { module: collection, parent_id: row.id } });
                      if (files && files.length) {
                        const f = files[0];
                        const token = localStorage.getItem("token");
                        const audit = localStorage.getItem("audit_tenant_id");
                        const headers = { Authorization: `Bearer ${token}` };
                        if (audit) headers["X-Audit-Tenant-Id"] = audit;
                        const res = await fetch(`${BACKEND_URL}/api/files/${f.id}/download`, { headers });
                        if (!res.ok) return;
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url; a.download = f.filename || `${collection}-${row.id}`; a.click();
                        URL.revokeObjectURL(url);
                      }
                    } catch { /* ignore */ }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {visibleFields.map((f) => {
                    const raw = row[f.key];
                    const display = f.render ? f.render(raw, row) :
                      f.type === "status" ? <StatusBadge value={raw} /> :
                      f.type === "date" && raw ? new Date(raw).toLocaleDateString("es-MX") :
                      f.type === "checkbox" ? (raw ? "Sí" : "No") :
                      raw ?? "-";
                    const tooltipText = typeof raw === "string" && raw.length > 40 ? raw : undefined;
                    return (
                      <td key={f.key} title={tooltipText} style={{
                        maxWidth: 280,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        verticalAlign: "top",
                      }}>{display}</td>
                    );
                  })}
                  {!hideEvidence && (
                    <td onClick={(e) => e.stopPropagation()} style={{ verticalAlign: "top" }}>
                      <EvidenceCell collection={collection} row={row} testIdPrefix={testIdPrefix} />
                    </td>
                  )}
                  <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "right" }}>
                    <div className="flex gap-1 justify-end">
                      {extraActions.map((a, i) => (
                        <button
                          key={i}
                          className="btn btn-sm"
                          onClick={() => a.onClick(row)}
                          title={a.label}
                          data-testid={a.testId ? a.testId(row) : `${testIdPrefix}-${a.label.toLowerCase().replace(/\s/g, "-")}-${row.id}`}
                        >
                          {a.icon}{a.icon ? " " : ""}{a.label}
                        </button>
                      ))}
                      <button className="btn btn-sm" onClick={() => openEdit(row)} data-testid={`${testIdPrefix}-edit-${row.id}`}>
                        <Pencil size={12} />
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => del(row)} data-testid={`${testIdPrefix}-del-${row.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? "Editar registro" : "Nuevo registro"}
        size="lg"
        testId={`${testIdPrefix}-modal`}
      >
        <form onSubmit={save} className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            {fields.filter((f) => !f.hiddenInForm).map((f) => (
              <div key={f.key} style={f.full ? { gridColumn: "1 / -1" } : {}}>
                <label className="label">{f.label}{f.required && " *"}</label>
                {f.type === "textarea" ? (
                  <textarea
                    className="textarea"
                    rows={3}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    required={f.required}
                    data-testid={`${testIdPrefix}-field-${f.key}`}
                  />
                ) : f.type === "select" ? (
                  <select
                    className="select"
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    required={f.required}
                    data-testid={`${testIdPrefix}-field-${f.key}`}
                  >
                    <option value="">-- Seleccione --</option>
                    {(f.options || []).map((o) => (
                      <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
                        {typeof o === "string" ? o : o.label}
                      </option>
                    ))}
                  </select>
                ) : f.type === "checkbox" ? (
                  <div>
                    <input
                      type="checkbox"
                      checked={!!form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                      data-testid={`${testIdPrefix}-field-${f.key}`}
                    /> {f.helper || ""}
                  </div>
                ) : (
                  <input
                    type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    className="input"
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })}
                    required={f.required}
                    step={f.type === "number" ? "any" : undefined}
                    data-testid={`${testIdPrefix}-field-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Archivos y evidencias — visible only when editing an existing record (needs an id) */}
          {editing && editing.id && (
            <div className="card" style={{ background: "var(--bg-2)", padding: 12, marginTop: 8 }}>
              <div className="card-title flex items-center gap-2" style={{ marginBottom: 8 }}>
                <Paperclip size={14} /> Archivos y evidencias
              </div>
              <FileCCE module={collection} parentId={editing.id} />
            </div>
          )}

          <div className="flex justify-end gap-2 mt-3">
            <button type="button" className="btn" onClick={() => setShowModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" data-testid={`${testIdPrefix}-save`}>
              {editing ? "Actualizar" : "Crear"}
            </button>
          </div>
        </form>
      </Modal>

      <UniversalImport
        collection={collection}
        fields={fields}
        parent={parent}
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => load()}
      />

      {/* Item 1/6/7/16/18 — Preview attachments on row click */}
      <Modal
        open={!!previewRow}
        onClose={() => setPreviewRow(null)}
        title={previewRow ? `Vista previa — ${previewRow.title || previewRow.name || previewRow.code || previewRow.description?.slice(0, 60) || previewRow.id}` : ""}
        size="xl"
        testId={`${testIdPrefix}-preview-modal`}
      >
        {previewRow && (
          <div>
            <div className="flex justify-end gap-2 mb-2">
              <button className="btn btn-sm btn-primary" onClick={() => { setEditing(previewRow); setForm({ ...previewRow }); setPreviewRow(null); setShowModal(true); }} data-testid={`${testIdPrefix}-preview-edit`}>
                <Pencil size={12} /> Editar registro
              </button>
              <button className="btn btn-sm" onClick={() => window.print()} data-testid={`${testIdPrefix}-preview-print`}>
                🖨️ Imprimir
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3" style={{ fontSize: 13 }}>
              {fields.filter((f) => !f.hidden).map((f) => {
                const raw = previewRow[f.key];
                if (raw == null || raw === "") return null;
                const display = f.render ? f.render(raw, previewRow) :
                  f.type === "date" && raw ? new Date(raw).toLocaleDateString("es-MX") :
                  f.type === "checkbox" ? (raw ? "Sí" : "No") :
                  String(raw);
                return (
                  <div key={f.key} style={f.full ? { gridColumn: "1 / -1" } : undefined}>
                    <div className="text-xs text-dim">{f.label}</div>
                    <div>{display}</div>
                  </div>
                );
              })}
            </div>
            <div className="card" style={{ background: "var(--bg-2)", padding: 10 }}>
              <div className="card-title flex items-center gap-2" style={{ marginBottom: 6 }}>
                <Paperclip size={14} /> Archivos adjuntos
              </div>
              <FileCCE module={collection} parentId={previewRow.id} />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
